// The browser's view of the API. Every write carries the CSRF token from /api/me and an
// Idempotency-Key the caller keeps for retries; the session is an HttpOnly cookie the page
// never touches, and nothing is stored in local storage.

import {
  type CreateRequestBodyInput,
  CreateRequestResponseSchema,
  type Me,
  MeSchema,
  RequestDetailSchema,
  RequestListSchema,
} from "@hoffman-support/contracts";

export interface ApiFailure {
  status: number;
  code: string;
  message: string;
  issues?: Array<{ path: string; message: string }>;
  rules?: string[];
}

export class ApiError extends Error {
  readonly failure: ApiFailure;
  constructor(failure: ApiFailure) {
    super(failure.message);
    this.failure = failure;
  }
}

async function failureOf(res: Response): Promise<ApiFailure> {
  try {
    const body = (await res.json()) as {
      error?: { code?: string; message?: string; rules?: string[] };
      issues?: ApiFailure["issues"];
    };
    return {
      status: res.status,
      code: body.error?.code ?? "unknown",
      message: body.error?.message ?? res.statusText,
      issues: body.issues,
      rules: body.error?.rules,
    };
  } catch {
    return { status: res.status, code: "unknown", message: res.statusText };
  }
}

async function json<T>(res: Response, parse: (v: unknown) => T): Promise<T> {
  if (!res.ok) throw new ApiError(await failureOf(res));
  return parse(await res.json());
}

export async function getMe(): Promise<Me | null> {
  const res = await fetch("/api/me", { credentials: "same-origin" });
  if (res.status === 401 || res.status === 503) return null;
  return json(res, (v) => MeSchema.parse(v));
}

export function listRequests(cursor?: string) {
  const q = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
  return fetch(`/api/requests${q}`, { credentials: "same-origin" }).then((r) =>
    json(r, (v) => RequestListSchema.parse(v)),
  );
}

export function getRequest(id: string) {
  return fetch(`/api/requests/${encodeURIComponent(id)}`, { credentials: "same-origin" }).then(
    (r) => json(r, (v) => RequestDetailSchema.parse(v)),
  );
}

function write(path: string, me: Me, idempotencyKey: string, body: unknown) {
  return fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      "x-csrf-token": me.csrfToken,
      "idempotency-key": idempotencyKey,
    },
    body: JSON.stringify(body),
  });
}

export function createRequest(me: Me, idempotencyKey: string, body: CreateRequestBodyInput) {
  return write("/api/requests", me, idempotencyKey, body).then((r) =>
    json(r, (v) => CreateRequestResponseSchema.parse(v)),
  );
}

export function replyToRequest(me: Me, idempotencyKey: string, id: string, body: string) {
  return write(`/api/requests/${encodeURIComponent(id)}/messages`, me, idempotencyKey, {
    body,
  }).then((r) => json(r, (v) => v as { status: string; reopened: boolean; redacted: boolean }));
}

export function withdrawRequest(me: Me, idempotencyKey: string, id: string, reason?: string) {
  return write(
    `/api/requests/${encodeURIComponent(id)}/withdraw`,
    me,
    idempotencyKey,
    reason ? { reason } : {},
  ).then((r) => json(r, (v) => v as { status: "withdrawn" }));
}

export async function signOut(me: Me): Promise<void> {
  await fetch("/auth/logout", {
    method: "POST",
    credentials: "same-origin",
    headers: { "x-csrf-token": me.csrfToken },
  });
}
